<script lang="ts">
	import { Icon } from '@repo/commons/components'
	import { tick } from 'svelte'

	type Props = {
		row: Record<string, string> | null
		key: string
		column: string
		editableEnabled: boolean
		shouldHighlightCurlyBraces?: boolean
		onsubmit: ((row: Record<string, string>) => void) | null
		onfocus: (() => void) | null
		onblur: (() => void) | null
		onstartediting?: (() => void) | null
	}

	let {
		row = null,
		key,
		column,
		editableEnabled = false,
		shouldHighlightCurlyBraces = false,
		onsubmit = null,
		onblur = null,
		onfocus = null,
		onstartediting = null
	}: Props = $props()

	let inputRef: HTMLDivElement | null = null
	let enableInputEditing: boolean = $state(false)
	let originalValue: string = row?.[key] ?? ''
	let skipBlur = false

	/** Enables editing mode and stores the original value */
	const onRowEdit = (e?: MouseEvent) => {
		if (!editableEnabled) {
			return
		}
		e?.stopPropagation()
		enableInputEditing = true
		originalValue = row?.[key] ?? '' // Save original value
		selectAllText() // Select all text in the input
		onfocus?.()
		onstartediting?.()
	}

	/** Dispatches an update and exits edit mode */
	const onSubmitRow = async () => {
		console.log(
			`sending from editable: ${JSON.stringify({ row: { ...row, [key]: inputRef?.textContent } })} Original was: ${originalValue}`
		)

		onsubmit?.({ ...row, [key]: inputRef?.textContent ?? '' })

		skipBlur = false
		await tick() // Ensure parent updates first
		onBlur() // Now safely blur
	}

	const handleEditCancel = () => {
		if (inputRef) {
			// Revert to original value
			inputRef.textContent = row?.[key] ?? ''
		}

		enableInputEditing = false
		onblur?.()
	}

	/** Handles pressing Enter (submit) */
	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			if (!e.shiftKey) {
				enableInputEditing = false
				onblur?.()
				onSubmitRow()
			}
		} else if (e.key === 'Escape') {
			e.preventDefault()
			handleEditCancel()
		}
	}

	/**
	 * Handles blur events by reverting edits if focus is lost.
	 * If skipBlur is true, the blur event is ignored.
	 * Otherwise, cancels any edits, disables editing mode and calls the onblur callback.
	 */
	const onBlur = () => {
		if (skipBlur) {
			skipBlur = false
			return
		}
		handleEditCancel()
		enableInputEditing = false
		onblur?.()
	}

	/** Handles clicking the close (cancel) button */
	const handleActionClick = (action: 'submit' | 'blur') => {
		// Skip blur event to prevent it from being called twice
		skipBlur = true
		if (action === 'submit') {
			onSubmitRow()
		} else if (action === 'blur') {
			handleEditCancel()
		}
	}

	const parseTextForCurlyBraces = (text: string): { text: string; highlight: boolean }[] => {
		// Parsing the text for anything within {} chars to ensure better styling.
		if (typeof text !== 'string') {
			return [{ text, highlight: false }]
		}
		const parts = []
		const regex = /\{(.*?)\}/g
		let lastIndex = 0
		let match

		while ((match = regex.exec(text)) !== null) {
			if (match.index > lastIndex) {
				parts.push({ text: text.slice(lastIndex, match.index), highlight: false })
			}
			parts.push({ text: match[1], highlight: true })
			lastIndex = regex.lastIndex
		}

		if (lastIndex < text.length) {
			parts.push({ text: text.slice(lastIndex), highlight: false })
		}

		return parts
	}

	const selectAllText = () => {
		if (inputRef) {
			const range = document.createRange()
			range.selectNodeContents(inputRef)

			const selection = window.getSelection()
			selection?.removeAllRanges()
			selection?.addRange(range)
		}
	}

	const onParentDblclick = () => {
		if (editableEnabled) {
			onRowEdit()
			if (inputRef) {
				inputRef.focus()
			}
		}
	}

	// TODO: Remove this once we have a proper key prop
	$effect(() => {
		if (column) {
			key = column
		}
	})
</script>

<div class="editable" role="button" tabindex="0" ondblclick={onParentDblclick}>
	<div
		class="element"
		contenteditable={enableInputEditing}
		role="textbox"
		tabindex="0"
		bind:this={inputRef}
		onblur={onBlur}
		onkeydown={handleKeyDown}
		ondblclick={onRowEdit}
	>
		{#if shouldHighlightCurlyBraces && !enableInputEditing}
			{#each parseTextForCurlyBraces(row?.[key] ?? ' ') as part}
				{#if part.highlight}
					<span class="highlighted-text">{part.text}</span>
				{:else}
					{part.text}
				{/if}
			{/each}
		{:else}
			{row?.[key] ?? ''}
		{/if}
	</div>
	<div class="actions">
		{#if enableInputEditing}
			<button
				class="action"
				onpointerdown={() => (skipBlur = true)}
				onclick={(e) => {
					e.stopPropagation()
					handleActionClick('blur')
				}}
				title="Cancel"
			>
				<div class="icon"><Icon name="close" size="xs" class="text-[#002C4D80]" /></div>
			</button>
			<button
				class="action"
				onpointerdown={() => (skipBlur = true)}
				onclick={(e) => {
					e.stopPropagation()
					handleActionClick('submit')
				}}
				title="Save"
			>
				<Icon name="checkCircle" size="md" variant="success" />
			</button>
		{/if}
	</div>
</div>

<style lang="postcss">
	.editable {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		gap: 20px;
		min-width: 50px;
		min-height: 75px;
	}

	.element {
		width: 100%;
		border: none;
		outline: none;
		min-height: 23px;
		display: block;
		word-break: break-word;
		user-select: text;
		-webkit-user-select: text;
		cursor: text;
	}

	.actions {
		display: flex;
		flex-direction: row;
		width: 48px;
	}

	.action {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 24px;
		height: 24px;
	}

	.icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 16px;
		height: 16px;
		border-radius: 8px;
		border: 1.5px solid #002c4d80;
	}

	.highlighted-text {
		@apply bg-se-green-1 text-white font-bold p-1 rounded-full shadow-lg border border-se-green-2;
	}
</style>
